import { NgTemplateOutlet } from '@angular/common';
import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  type ElementRef,
  inject,
  Injector,
  signal,
  viewChild,
} from '@angular/core';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { lucideEyeOff } from '@ng-icons/lucide';
import { BrnDialogRef, injectBrnDialogContext } from '@spartan-ng/brain/dialog';
import { HlmButton } from '@lite-workspace/shared/helm-ui/button';
import { DialogSheetComponent } from '@lite-workspace/shared/helm-ui/dialog';
import { HlmEmpty, HlmEmptyTitle } from '@lite-workspace/shared/helm-ui/empty';
import { HlmTooltip } from '@lite-workspace/shared/helm-ui/tooltip';
import { IconComponent } from '@lite-workspace/shared/blocks/icon';
import type { PlaylistLesson } from '../catalog.types';
import { OnboardingService } from '../onboarding.service';
import { LessonCardComponent } from './lesson-card.component';
import { LessonPlayerComponent } from './lesson-player.component';
import { OnboardingMediaService } from './onboarding-media.service';
import { searchLessons, type TextSegment } from './search-lessons';
import { supportChannels } from './support-channels';

/** Контекст открытия: на каком уроке открыть библиотеку; `null` — на уроке текущего экрана. */
export interface LessonLibraryContext {
  lessonId: string | null;
  autoplay: boolean;
}

const PLAIN = (text: string): TextSegment[] => [{ text, match: false }];

/** Заголовок верхнего блока списка: по нему же различаются две копии отложенного урока (R28, R31). */
const FAVORITES_BLOCK = 'Избранное';

/** Ролики, доступные с текущего экрана: они идут над остальными и во «Все видео» не повторяются (R48). */
const SCREEN_BLOCK = 'На этом экране';

const ALL_BLOCK = 'Все видео';

/** Играющий урок и блок списка, в котором его выбрали: подсветку берёт ровно эта копия (R31). */
interface LessonSelection {
  lesson: PlaylistLesson;
  block: string;
}

/** Выбор «скрыть просмотренные» живёт в браузере: профиль его не носит — это привычка устройства. */
const HIDE_VIEWED_KEY = 'onboarding.lessons.hideViewed';

function readHideViewed(): boolean {
  try {
    return localStorage.getItem(HIDE_VIEWED_KEY) === 'true';
  } catch {
    /* хранилище закрыто (приватное окно) — переключатель начинает выключенным */
    return false;
  }
}

/** Верх строки урока на экране, строка ищется по ключу `rowKey`. */
function rowTop(list: HTMLElement, key: string): number | undefined {
  const rows = Array.from(list.querySelectorAll<HTMLElement>('[data-row]'));

  return rows.find(row => row.dataset['row'] === key)?.getBoundingClientRect().top;
}

function storeHideViewed(hide: boolean): void {
  try {
    localStorage.setItem(HIDE_VIEWED_KEY, String(hide));
  } catch {
    /* хранилище закрыто — выбор живёт до закрытия окна */
  }
}

/**
 * Библиотека уроков: слева поиск и список уроков, справа плеер — один кадр, без панели под ним. Порядок списка — порядок
 * плейлиста, один в один. Сверху «Избранное» — если хоть один урок отложен, и отложенный урок стоит
 * в нём и ниже (R28); под ним «На этом экране» — ролики, доступные с текущего экрана, в порядке флоу
 * и без повторов; в «Все видео» уходит всё остальное (R48). Ни избранного, ни уроков экрана —
 * заголовков нет, список плоский.
 * Блоки живут порознь: горит та копия урока, по которой нажали, вторая остаётся обычной карточкой (R31).
 * Урок, с которым окно открыли, играет сразу и подсвечен на своём месте; экран задаёт только урок по умолчанию.
 * Кнопка-глаз рядом с полем поиска убирает просмотренные уроки — кроме играющего, — а выдачу поиска
 * не трогает: поиск идёт по всем урокам, по названию и по описанию.
 */
@Component({
  selector: 'onboarding-lesson-library',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    DialogSheetComponent,
    HlmButton,
    HlmEmpty,
    HlmEmptyTitle,
    HlmTooltip,
    IconComponent,
    LessonCardComponent,
    LessonPlayerComponent,
    NgIcon,
    NgTemplateOutlet,
  ],
  viewProviders: [provideIcons({ lucideEyeOff })],
  host: {
    class: `block max-md:contents md:relative md:w-[var(--dlg-w-lessons)] md:overflow-hidden
            md:rounded-[var(--r-xl)] md:border md:border-line md:bg-panel md:shadow-[var(--shadow-4)]`,
    '[attr.aria-labelledby]': 'titleId',
  },
  styles: `
    /* Список едет без полосы: она резала бы карточки у правого края. Прокрутка колесом и клавишами цела. */
    .list-scroll {
      scrollbar-width: none;
      /* Сдвиг от сердечка гасит toggleFavorite сам: подстройка браузера погасила бы его второй раз. */
      overflow-anchor: none;
    }
    .list-scroll::-webkit-scrollbar {
      display: none;
    }
  `,
  template: `
    <hlm-dialog-sheet flush>
      <ng-template>
        @if (!lessons().length) {
          <div hlmEmpty>
            <p hlmEmptyTitle [id]="titleId">Уроки скоро появятся</p>
          </div>
          <ng-container [ngTemplateOutlet]="supportRow" />
        } @else {
          <div class="flex w-full max-md:flex-col md:h-[var(--dlg-h-lessons)]">
            <!-- Высота окна на широком экране — доля экрана, кадр справа занимает её целиком; список
                 выведен из потока и прокручивается внутри этой высоты. Полоса поддержки —
                 отдельный этаж колонки под прокруткой: место под неё занято всегда, список едет над
                 ней и под неё не заезжает. Список лежит на своей поверхности, на тон ниже окна. -->
            <aside
              class="shrink-0 bg-panel2 max-md:order-last max-md:w-full md:absolute md:inset-y-0 md:left-0 md:flex
                     md:w-90 md:flex-col md:overflow-hidden md:border-r md:border-line2"
            >
              <div #listScroll class="list-scroll px-4.5 pb-2 pt-5 max-md:px-0 md:min-h-0 md:flex-1 md:overflow-auto">
                <div class="flex items-center gap-2">
                  <label
                    class="flex h-9.5 min-w-0 flex-1 items-center gap-2.25 rounded-[var(--r)] bg-coach-wash px-3
                           focus-within:ring-2 focus-within:ring-sel"
                  >
                    <app-icon name="search" [size]="16" class="text-c3" />
                    <input
                      class="min-w-0 flex-1 border-0 bg-transparent text-catalog-subheading text-coach-ink outline-none
                             placeholder:text-c3"
                      type="search"
                      placeholder="Найти урок"
                      aria-label="Найти урок"
                      [value]="query()"
                      (input)="onQuery($event)"
                    />
                  </label>

                  <!-- Фильтр правит только список разделов: выдача поиска показывает и просмотренные.
                       Просмотренных нет — скрывать нечего: кнопки нет, поле поиска берёт всю строку (T8.8). -->
                  @if (anyViewed()) {
                    <button
                      hlmBtn
                      variant="ghost"
                      size="icon-lg"
                      type="button"
                      class="shrink-0 rounded-[var(--r)] pointer-coarse:size-11"
                      aria-label="Скрыть просмотренные видео"
                      hlmTooltip="Скрыть просмотренные видео"
                      [class.bg-acc-soft]="hideViewed()"
                      [attr.aria-pressed]="hideViewed()"
                      (click)="setHideViewed(!hideViewed())"
                    >
                      <ng-icon name="lucideEyeOff" size="16" />
                    </button>
                  }
                </div>

                @if (hits(); as found) {
                  @if (found.length) {
                    <p class="mx-1 mb-2.5 mt-5 text-catalog-caption font-medium text-c3">Найдено: {{ found.length }}</p>
                    <!-- Выдача встаёт теми же блоками, что и список: ничего не нашлось среди
                         отложенных — блока «Избранное» нет вовсе (R28). -->
                    @for (group of hitGroups(); track group.title) {
                      <div class="mt-6.5 first:mt-0">
                        @if (group.title) {
                          <h3 class="mx-1 mb-3 text-catalog-caption font-medium text-c3">{{ group.title }}</h3>
                        }
                        <div data-testid="lesson-rows" class="-my-1 flex flex-col gap-0">
                          @for (hit of group.items; track hit.lesson.id) {
                            <div
                              onboarding-lesson-card
                              class="w-full"
                              [titleSegments]="hit.titleSegments"
                              [snippetSegments]="hit.snippetSegments"
                              [snippetLead]="lead(hit.lesson, hit.snippetSegments)"
                              [snippetTail]="tail(hit.lesson, hit.snippetSegments)"
                              [meta]="meta(hit.lesson, group.title)"
                              [poster]="poster(hit.lesson.id)"
                              [viewed]="viewed(hit.lesson.id)"
                              [active]="playing(hit.lesson, group.title)"
                              [current]="playing(hit.lesson, group.title)"
                              [playing]="nowPlaying(hit.lesson, group.title)"
                              [attr.data-row]="rowKey(hit.lesson.id, group.title)"
                              (click)="select(hit.lesson, group.title)"
                            ></div>
                          }
                        </div>
                      </div>
                    }
                  } @else {
                    <p class="mx-1 my-5.5 text-catalog-subheading text-c3">Ничего не нашлось. Попробуйте другое слово.</p>
                  }
                } @else {
                  <!-- Порядок плейлиста, один в один. Сверху «Избранное» с отложенными уроками, под
                       ним «На этом экране» — ролики текущего экрана в порядке флоу, — и «Все видео»
                       со всем остальным, без повтора показанного выше; ни того, ни другого —
                       заголовков нет, список плоский (R18, R28, R48). -->
                  <!-- Строки вплотную: 8px между подложками — поля py-1 самих строк, и курсор между ними
                       не мигает. -my-1 снимает крайние поля, отступы разделов и заголовков — как были. -->
                  <div class="mt-6.5">
                    @for (group of groups(); track group.title) {
                      <div class="mt-6.5 first:mt-0">
                        @if (group.title) {
                          <h3 class="mx-1 mb-3 text-catalog-caption font-medium text-c3">{{ group.title }}</h3>
                        }
                        <div data-testid="lesson-rows" class="-my-1 flex flex-col gap-0">
                          @for (lesson of group.items; track lesson.id) {
                            <!-- Строка — не кнопка: внутри неё своя кнопка выбора. -->
                            <div
                              onboarding-lesson-card
                              class="w-full"
                              [titleSegments]="title(lesson)"
                              [meta]="meta(lesson, group.title)"
                              [poster]="poster(lesson.id)"
                              [viewed]="viewed(lesson.id)"
                              [active]="playing(lesson, group.title)"
                              [current]="playing(lesson, group.title)"
                              [playing]="nowPlaying(lesson, group.title)"
                              [attr.data-row]="rowKey(lesson.id, group.title)"
                              (click)="select(lesson, group.title)"
                            ></div>
                          }
                        </div>
                      </div>
                    }
                  </div>
                  <!-- Смотреть больше нечего: строка встаёт под последней оставшейся карточкой. -->
                  @if (hideViewed() && !remaining()) {
                    <div hlmEmpty>
                      <p hlmEmptyTitle>Вы посмотрели все уроки</p>
                      <button hlmBtn variant="outline" size="sm" type="button" (click)="setHideViewed(false)">
                        Показать все
                      </button>
                    </div>
                  }
                }
              </div>

              <ng-container [ngTemplateOutlet]="supportRow" />
            </aside>

            <!-- Под кадром ничего нет: сердечко и отметка просмотра стоят над кадром, рядом с закрытием. -->
            <div class="relative min-w-0 flex-1 md:ml-90">
              @if (selected(); as lesson) {
                <!-- Название урока окно не рисует — его видно в списке; оно только подписывает окно для читалки. -->
                <h3 class="sr-only" [id]="titleId">{{ lesson.title }}</h3>
                <!-- Плеер вне ключа: смена урока меняет только вход, выбранная скорость остаётся. -->
                <onboarding-lesson-player
                  [lesson]="lesson"
                  [autoplay]="autoplay()"
                  (playedToEnd)="onboarding.markViewed(lesson.id)"
                />
                <!-- Телефон: ряда над окном там нет, отметка и сердечко встают в угол самого кадра.
                     Закрытие не нужно — лист закрывается жестом вниз. -->
                <div data-testid="lesson-actions-phone" class="absolute right-3.5 top-3.5 z-10 flex items-center gap-2 md:hidden">
                  <ng-container [ngTemplateOutlet]="lessonToggles" [ngTemplateOutletContext]="{ $implicit: lesson }" />
                </div>
              }
            </div>
          </div>
        }

        <!-- Одна карточка на оба состояния окна: под списком и под «Уроки скоро появятся». Место под неё
             занято всегда, поэтому спросить можно, не долистав до конца. Каналов нет — карточки нет.
             Поле по бокам равно полю списка; карточка на тон светлее колонки списка (R35). -->
        <ng-template #supportRow>
          @if (channels().length) {
            <div class="flex-none px-4.5 py-3 max-md:px-0">
              <div
                data-testid="support-card"
                class="flex h-16 items-center justify-between rounded-[var(--r-l)] bg-panel3 px-3.5"
              >
                <span class="text-sm text-ink">Поддержка</span>
                <!-- Каждый канал — своя пилюля: нажатие открывает переписку сразу, без выпадающего меню между
                     вопросом и ответом (R35). Порядок пилюль — порядок каналов плейлиста. -->
                <div class="flex items-center gap-2">
                  @for (channel of channels(); track channel.kind) {
                    <button
                      hlmBtn
                      variant="outline"
                      size="sm"
                      type="button"
                      class="rounded-full border-ink-70 bg-transparent px-3.5 font-medium text-ink hover:border-primary
                             hover:bg-primary hover:text-primary-foreground"
                      (click)="channel.open()"
                    >
                      {{ channel.label }}
                    </button>
                  }
                </div>
              </div>
            </div>
          }
        </ng-template>
      </ng-template>
    </hlm-dialog-sheet>

    <!-- Действия над кадром: отметка просмотра и избранное играющего урока, затем закрытие — круги-скримы с белой
         рамкой, читаются и на тёмном, и на светлом кадре (T8.12). -->
    <div data-testid="lesson-actions" class="absolute right-3.5 top-3.5 z-10 hidden items-center gap-2 md:flex">
      @if (selected(); as lesson) {
        <ng-container [ngTemplateOutlet]="lessonToggles" [ngTemplateOutletContext]="{ $implicit: lesson }" />
      }
      <button
        hlmBtn
        variant="on-media-ring"
        size="icon-lg"
        type="button"
        aria-label="Закрыть"
        (click)="dialogRef.close(undefined)"
      >
        <app-icon name="close" [size]="18" />
      </button>
    </div>

    <!-- Одни и те же отметка и сердечко: на широком экране в ряду над окном, на телефоне — в углу кадра. -->
    <ng-template #lessonToggles let-lesson>
      <button
        hlmBtn
        variant="on-media-ring"
        size="icon-lg"
        type="button"
        class="pointer-coarse:size-11"
        [attr.data-state]="viewed(lesson.id) ? 'on' : null"
        [attr.aria-pressed]="viewed(lesson.id)"
        [attr.aria-label]="viewed(lesson.id) ? 'Отметить непросмотренным' : 'Отметить просмотренным'"
        [hlmTooltip]="viewed(lesson.id) ? 'Отметить непросмотренным' : 'Отметить просмотренным'"
        (click)="toggleViewed(lesson.id)"
      >
        <app-icon name="check" [size]="18" />
      </button>
      <button
        hlmBtn
        variant="on-media-ring"
        size="icon-lg"
        type="button"
        class="pointer-coarse:size-11"
        [attr.aria-pressed]="favorite(lesson.id)"
        [attr.aria-label]="favorite(lesson.id) ? 'Убрать из избранного' : 'В избранное'"
        [hlmTooltip]="favorite(lesson.id) ? 'Убрать из избранного' : 'В избранное'"
        (click)="toggleFavorite(lesson.id, '')"
      >
        <app-icon [name]="favorite(lesson.id) ? 'heart-filled' : 'heart'" [size]="18" />
      </button>
    </ng-template>
  `,
})
export class LessonLibraryComponent {
  protected readonly onboarding = inject(OnboardingService);
  private readonly media = inject(OnboardingMediaService);
  protected readonly dialogRef = inject(BrnDialogRef);
  private readonly context = injectBrnDialogContext<LessonLibraryContext>();
  private readonly injector = inject(Injector);

  protected readonly titleId = 'onboarding-lesson-title';

  private readonly listScroll = viewChild<ElementRef<HTMLElement>>('listScroll');
  private readonly player = viewChild(LessonPlayerComponent);

  /** Все уроки подряд, в порядке плейлиста: разделы рисует список, а поиск ищет по всему. */
  protected readonly lessons = this.onboarding.lessons;

  /**
   * Ролики, доступные с текущего экрана, — их отдаёт сервис. Снимок на момент открытия: маршрут за
   * спиной диалога блоки списка больше не перекладывает (T2).
   */
  private readonly screenLessonIds: ReadonlySet<string> = new Set(this.onboarding.screenLessons().map(lesson => lesson.id));

  /** Те же каналы, что и в меню «?»: ни одного — строки поддержки в окне нет (T5.2). */
  protected readonly channels = supportChannels();

  /**
   * Выбор — пара «урок + блок списка»: отложенный урок стоит в двух блоках, и горит тот, по которому
   * нажали. Выбор не нажатием (автозапуск, ссылка, первое открытие) приписан «Всем видео» (R31).
   */
  private readonly selection = signal<LessonSelection | null>(this.initialSelection());

  protected readonly selected = computed(() => this.selection()?.lesson ?? null);

  /**
   * Ролик заводится сам, только когда фотограф позвал именно его: нажал обложку на карточке коуча или
   * карточку урока в списке. Открытое иначе окно — дверь в шапке, меню «?», всплывшее промо, первый
   * визит — ждёт на постере, и играть начинает его нажатие (R41).
   */
  protected readonly autoplay = signal(this.context?.autoplay ?? false);

  protected readonly query = signal('');

  /** «Скрыть просмотренные»: выбор прошлого захода — окно открывается таким, каким его закрыли. */
  protected readonly hideViewed = signal(readHideViewed());

  constructor() {
    // Карточка поддержки открылась вместе с окном: ссылку в Telegram минтим сейчас, к нажатию она готова.
    for (const channel of this.channels()) channel.prepare?.();
    // Окно открыли на уроке — строка урока сразу встаёт вверху списка; открыли просто — список с начала.
    // Фаза `read` — последняя: окно само наводит фокус на поле поиска в фазе до неё, а браузер ради
    // фокуса возвращает список к началу. Прокрутка встаёт после этого возврата, иначе он её стёр бы.
    if (this.context?.lessonId) afterNextRender({ read: () => this.revealSelected() });
  }

  /** Сколько уроков ещё не смотрели — по всему списку: строка «смотреть больше нечего» идёт за ним. */
  protected readonly remaining = computed(() => {
    const viewed = this.onboarding.viewedLessons();

    return this.lessons().filter(lesson => !viewed.has(lesson.id)).length;
  });

  /** Хоть один урок просмотрен — только тогда есть что скрывать кнопкой-глазом (T8.8). */
  protected readonly anyViewed = computed(() => this.remaining() < this.lessons().length);

  /**
   * Список под переключателем — те же уроки и в том же порядке, что отдал плейлист: под фильтром
   * из него уходят просмотренные. Играющий урок остаётся и просмотренным — иначе список вынул бы
   * из-под фотографа тот самый ролик, который он сейчас смотрит.
   */
  protected readonly listLessons = computed<readonly PlaylistLesson[]>(() => {
    const lessons = this.lessons();
    if (!this.hideViewed()) return lessons;

    const viewed = this.onboarding.viewedLessons();
    const playing = this.selected()?.id;

    return lessons.filter(lesson => !viewed.has(lesson.id) || lesson.id === playing);
  });

  protected readonly hits = computed(() => searchLessons(this.lessons(), this.query()));

  /** Блоки списка: «Избранное», «На этом экране», «Все видео» — или один блок без заголовка (R28, R48). */
  protected readonly groups = computed(() => this.split(this.listLessons(), lesson => lesson.id, this.lessons()));

  /** Есть что вынести в блок — список идёт с заголовками; нечего — он плоский (R28, R48). */
  private readonly grouped = computed(() => {
    const favorites = this.onboarding.favoriteLessons();

    return this.screenLessonIds.size > 0 || this.lessons().some(lesson => favorites.has(lesson.id));
  });

  /** Те же блоки в выдаче поиска: фильтр работает внутри каждого. */
  protected readonly hitGroups = computed(() => this.split(this.hits() ?? [], hit => hit.lesson.id));

  /**
   * Сегменты держатся по ссылке: новый массив на каждый цикл сверки грязнил бы вход карточки.
   * Ключ — сам заголовок, а не id: плейлист переписывает названия, и кэш по id отдал бы старое.
   */
  private readonly plainTitles = new Map<string, TextSegment[]>();

  protected title(lesson: PlaylistLesson): TextSegment[] {
    const cached = this.plainTitles.get(lesson.title);
    if (cached) return cached;

    const segments = PLAIN(lesson.title);
    this.plainTitles.set(lesson.title, segments);

    return segments;
  }

  /** Мета карточки: играющая объявляет себя, остальные — включая вторую копию урока — длительность. */
  protected meta(lesson: PlaylistLesson, block: string): string {
    const duration = this.media.duration(lesson.id);

    return this.nowPlaying(lesson, block) ? ['Сейчас играет', duration].filter(Boolean).join(' · ') : duration;
  }

  /**
   * Ролик этой карточки идёт прямо сейчас — так говорит сам плеер. Ролик на паузе (окно открыли на постере,
   * фотограф остановил его) подсветку и `aria-current` карточке оставляет, но «Сейчас играет» и пауза на
   * кадре врали бы (R41c, T8.7).
   */
  protected nowPlaying(lesson: PlaylistLesson, block: string): boolean {
    return this.playing(lesson, block) && (this.player()?.isPlaying() ?? false);
  }

  /** Окно отрывка вырезано из середины описания — края договаривает многоточие. */
  protected lead(lesson: PlaylistLesson, segments: readonly TextSegment[]): string {
    return lesson.description.startsWith(segments[0]?.text ?? '') ? '' : '…';
  }

  protected tail(lesson: PlaylistLesson, segments: readonly TextSegment[]): string {
    const last = segments[segments.length - 1]?.text ?? '';

    return lesson.description.endsWith(last) ? '' : '…';
  }

  /** Отметку просмотра ставит досмотр до конца или сам фотограф на строке урока; выбор урока её не ставит. */
  protected viewed(id: string): boolean {
    return this.onboarding.viewedLessons().has(id);
  }

  protected favorite(id: string): boolean {
    return this.onboarding.favoriteLessons().has(id);
  }

  /** Кадр строки — постер того же ролика, что играет плеер; ролика нет — кадра нет. */
  protected poster(id: string): string | null {
    return this.media.media(id)?.poster ?? null;
  }

  /**
   * Снята последняя отметка — кнопка-глаз уходит, и фильтр выключается с ней: список не остаётся
   * отфильтрованным за спрятанной кнопкой (T8.8).
   */
  protected toggleViewed(id: string): void {
    if (!this.viewed(id)) {
      this.onboarding.markViewed(id);

      return;
    }

    const last = this.remaining() === this.lessons().length - 1;
    this.onboarding.unmarkViewed(id);
    if (last) this.setHideViewed(false);
  }

  /**
   * Играющая карточка — ровно одна: отложенный урок стоит в двух блоках, и горит тот, по которому
   * нажали (R31). Урок вынули из избранного — его верхняя копия исчезла, и подсветка переезжает на
   * оставшуюся. Подсветка и `aria-current` идут вместе: читалка объявляет ту же карточку, что видна.
   */
  protected playing(lesson: PlaylistLesson, block: string): boolean {
    const selection = this.selection();
    if (!selection || lesson.id !== selection.lesson.id) return false;

    return block === this.blockOf(lesson.id, selection.block);
  }

  /**
   * Блок, в котором урок горит: тот, по которому нажали. Нажали в «Избранном» — горит верхняя копия;
   * иначе, и когда верхней копии не стало, горит единственная нижняя — в «На этом экране» у урока
   * экрана, во «Всех видео» у остальных, а в плоском списке заголовка у неё нет (R31, R48).
   */
  private blockOf(id: string, picked: string): string {
    if (picked === FAVORITES_BLOCK && this.favorite(id)) return FAVORITES_BLOCK;
    if (!this.grouped()) return '';

    return this.screenLessonIds.has(id) ? SCREEN_BLOCK : ALL_BLOCK;
  }

  /**
   * Разбивка на блоки: нечего отложить и нечего показать по экрану — один блок без заголовка, список
   * читается плоским. Иначе сверху «Избранное» в порядке плейлиста, под ним «На этом экране» в порядке
   * каталога — это и есть порядок флоу, — а «Все видео» держат всё, чего нет на экране: ролик, уже
   * показанный сверху, ниже не повторяется. Пустой блок не рисуется вместе со своим заголовком (R28, R48).
   * `favoriteSource` — откуда брать «Избранное»: фильтр просмотренных его не трогает (T2.2, T4.3).
   */
  private split<T>(
    items: readonly T[],
    idOf: (item: T) => string,
    favoriteSource: readonly T[] = items,
  ): { title: string; items: readonly T[] }[] {
    if (!this.grouped()) return [{ title: '', items }];

    const favorites = this.onboarding.favoriteLessons();
    const byId = new Map(items.map(item => [idOf(item), item]));

    return [
      { title: FAVORITES_BLOCK, items: favoriteSource.filter(item => favorites.has(idOf(item))) },
      {
        title: SCREEN_BLOCK,
        items: [...this.screenLessonIds].map(id => byId.get(id)).filter((item): item is T => item !== undefined),
      },
      { title: ALL_BLOCK, items: items.filter(item => !this.screenLessonIds.has(idOf(item))) },
    ].filter(group => group.items.length > 0);
  }

  /**
   * Сердечко в ряду над кадром: урок встаёт в «Избранное» сверху или уходит из него (R28). Блок сверху
   * растёт или тает, а список докручивается на тот же сдвиг — нижняя строка урока стоит на месте (T8.4).
   * Предел: список у самого верха выше нуля не докрутить — при снятии строка там поднимается на остаток.
   */
  protected toggleFavorite(id: string, block: string): void {
    const list = this.listScroll()?.nativeElement;
    const key = this.rowKey(id, block);
    const before = list ? rowTop(list, key) : undefined;
    this.onboarding.toggleFavorite(id);
    if (!list || before === undefined) return;

    afterNextRender(
      {
        read: () => {
          const after = rowTop(list, key);
          if (after !== undefined) list.scrollTop += after - before;
        },
      },
      { injector: this.injector },
    );
  }

  /**
   * Ключ строки (`data-row`, якорь докрутки): копия в «Избранном» своя, нижняя — одна на урок, какой бы
   * блок её ни держал: с первым отложенным уроком плоский список встаёт под заголовки, и строка меняет блок.
   */
  protected rowKey(id: string, block: string): string {
    return block === FAVORITES_BLOCK ? `${FAVORITES_BLOCK}/${id}` : id;
  }

  /**
   * Нажатие по карточке — это и есть просьба показать ролик: со второго урока кадр заводится сам.
   * Нажатие по строке урока, что уже в плеере, — любой его копии — ставит ролик на паузу и снова пускает (T8.7).
   */
  protected select(lesson: PlaylistLesson, block: string): void {
    const again = lesson.id === this.selected()?.id;
    this.selection.set({ lesson, block });
    this.autoplay.set(true);
    if (again) this.player()?.togglePlayback();
  }

  protected setHideViewed(hide: boolean): void {
    this.hideViewed.set(hide);
    storeHideViewed(hide);
  }

  protected onQuery(event: Event): void {
    this.query.set((event.target as HTMLInputElement).value);
  }

  /**
   * Строка выбранного урока — вверх окна списка, под его верхним полем, без анимации. Крутится только
   * сам список: `scrollIntoView` потащил бы за собой и окно, и страницу. На телефоне список не
   * прокручивается сам по себе — присваивание там ничего не делает.
   */
  private revealSelected(): void {
    const list = this.listScroll()?.nativeElement;
    const row = list?.querySelector<HTMLElement>('[aria-current="true"]');
    if (!list || !row) return;

    const padding = parseFloat(getComputedStyle(list).paddingTop) || 0;
    const box = list.getBoundingClientRect();
    // Окно ещё въезжает с уменьшения: коробки на экране сжаты, а прокрутка меряется в несжатых пикселях.
    const scale = list.offsetHeight ? box.height / list.offsetHeight : 1;
    list.scrollTop += (row.getBoundingClientRect().top - box.top) / scale - padding;
  }

  /**
   * Запрошенный урок, иначе первый урок этого экрана, иначе первый урок списка. Выбрали его не
   * нажатием — горит его нижняя копия, не та, что в «Избранном» (R31).
   */
  private initialSelection(): LessonSelection | null {
    const lessons = this.lessons();
    const requested = lessons.find(lesson => lesson.id === this.context?.lessonId);
    const lesson = requested ?? this.onboarding.screenLessons()[0] ?? lessons[0] ?? null;

    return lesson ? { lesson, block: ALL_BLOCK } : null;
  }
}
